import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client.js';
import { useNotificationPolling } from '../hooks/useNotificationPolling.js';
import '../styles/pages/notifications.css';

/**
 * Returns a human-readable relative time string.
 * e.g. "just now", "2 minutes ago", "1 hour ago", "3 days ago"
 */
export function timeAgo(dateString) {
  if (!dateString) return '';
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;

  if (diffMs < 0) return 'just now';

  const diffSeconds = Math.floor(diffMs / 1000);
  if (diffSeconds < 60) return 'just now';

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }

  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) {
    return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`;
  }

  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

export default function NotificationBell() {
  const { unreadCount, refresh } = useNotificationPolling();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Fetch recent notifications for the dropdown
  const fetchRecent = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/notifications?limit=5');
      setNotifications(data?.notifications ?? data ?? []);
    } catch {
      // Silently fail — don't break UI on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Open/close dropdown
  function handleBellClick() {
    if (!open) {
      fetchRecent();
    }
    setOpen((prev) => !prev);
  }

  // Mark a single notification as read and navigate to deepLinkUrl
  async function handleNotificationClick(notification) {
    setOpen(false);
    try {
      if (notification.status === 'unread') {
        await apiClient.put(`/api/notifications/${notification.id}/read`, {});
        refresh();
      }
    } catch {
      // Silently fail — navigation should still proceed
    }
    if (notification.deepLinkUrl) {
      const url = notification.deepLinkUrl;
      if (typeof url === 'string' && url.startsWith('/') && !url.startsWith('//') && !url.includes('://')) {
        navigate(url);
      }
    }
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handleOutsideClick(e) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const ariaLabel = unreadCount > 0
    ? `Notifications, ${unreadCount} unread`
    : 'Notifications';

  return (
    <div className="notif-bell-wrapper">
      <button
        ref={buttonRef}
        className="notif-bell-btn"
        onClick={handleBellClick}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {/* Bell SVG icon */}
        <svg
          className="notif-bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {/* Badge — only shown when count > 0 */}
        {unreadCount > 0 && (
          <span className="notif-bell-badge" aria-hidden="true">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown popover */}
      {open && (
        <div
          ref={dropdownRef}
          className="notif-dropdown"
          role="dialog"
          aria-label="Notifications"
        >
          <div className="notif-dropdown__header">
            <span className="notif-dropdown__title">Notifications</span>
            {unreadCount > 0 && (
              <span className="notif-dropdown__unread-count">
                {unreadCount} unread
              </span>
            )}
          </div>

          <div className="notif-dropdown__body">
            {loading ? (
              <div className="notif-dropdown__loading">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="notif-dropdown__empty">No notifications yet</div>
            ) : (
              <ul className="notif-dropdown__list" role="list">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      className={`notif-dropdown__item${n.status === 'unread' ? ' notif-dropdown__item--unread' : ''}`}
                      onClick={() => handleNotificationClick(n)}
                    >
                      {n.status === 'unread' && (
                        <span className="notif-unread-dot" aria-label="Unread" />
                      )}
                      <div className="notif-dropdown__item-content">
                        <span className="notif-dropdown__item-title">
                          {n.title}
                        </span>
                        {n.body && (
                          <span className="notif-dropdown__item-body">
                            {n.body}
                          </span>
                        )}
                        <span className="notif-dropdown__item-time">
                          {timeAgo(n.createdAt)}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="notif-dropdown__footer">
            <button
              className="notif-dropdown__see-all"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
            >
              See all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
